import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Lightbulb, Target, Shield, Zap, Phone, Calendar, FileText, UserCheck, Search, Settings, Clock } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Consultation = () => {
  const helpWith = [
    {
      icon: Target,
      title: "IP Acquisition & Strategy",
      description: "Help clients get IP blocks when others said no—through proper justification and strategy.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Users,
      title: "Carrier Contract Negotiation", 
      description: "Saved companies hundreds of thousands by renegotiating smarter deals with Tier 1/2 providers.",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: Lightbulb,
      title: "Proxy Software Architecture",
      description: "Built full proxy management systems with dashboards, resell features, and IP control.",
      gradient: "from-amber-500 to-orange-500"
    },
    {
      icon: Shield,
      title: "Compliance & Legal Guidance",
      description: "Helped clients navigate DMCA, GDPR, and platform-specific abuse policies.",
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      icon: Zap,
      title: "Crisis Engineering & Recovery",
      description: "Rebuilt broken systems, solved fires, and helped companies regain control.",
      gradient: "from-red-500 to-rose-500"
    },
    {
      icon: Target,
      title: "Infrastructure Planning",
      description: "Strategic guidance on server placement, network design, and scalability planning.",
      gradient: "from-indigo-500 to-blue-500"
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Discovery Call",
      description: "We assess your goals and current setup."
    },
    {
      step: "2", 
      title: "Strategic Roadmap",
      description: "Clear direction and actionable next steps."
    },
    {
      step: "3",
      title: "Execute", 
      description: "Hands-on support or guidance to implement."
    }
  ];

  const formats = [
    {
      title: "1-on-1 Strategy Sessions",
      description: "Deep dive strategic planning tailored to your specific challenges",
      icon: Users,
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      title: "Vendor & Architecture Audits", 
      description: "Comprehensive analysis of your current infrastructure setup",
      icon: Search,
      gradient: "from-purple-500 to-pink-500"
    },
    {
      title: "Compliance Walkthroughs",
      description: "Navigate complex regulations with expert guidance", 
      icon: Shield,
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      title: "Contract Reviews",
      description: "Professional contract analysis to maximize your leverage",
      icon: FileText,
      gradient: "from-amber-500 to-orange-500"
    },
    {
      title: "Custom Server Build Planning",
      description: "Strategic hardware planning for optimal performance",
      icon: Settings,
      gradient: "from-red-500 to-rose-500"
    },
    {
      title: "Long-Term Advisory Retainers",
      description: "Ongoing strategic partnership for continuous growth",
      icon: Clock,
      gradient: "from-indigo-500 to-blue-500"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden section-float">
        {/* Enhanced Animated Background - Consistent with What We Help With section */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Gradient Orbs */}
          <div className="absolute top-1/4 left-1/6 w-32 h-32 bg-gradient-to-br from-nexx-green/20 to-emerald-400/20 rounded-full blur-xl animate-float-up" style={{ animationDuration: '8s', animationDelay: '0s' }}></div>
          <div className="absolute top-2/3 right-1/4 w-40 h-40 bg-gradient-to-br from-blue-400/15 to-cyan-400/15 rounded-full blur-xl animate-float-up" style={{ animationDuration: '12s', animationDelay: '2s' }}></div>
          <div className="absolute bottom-1/4 left-3/4 w-28 h-28 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-float-up" style={{ animationDuration: '10s', animationDelay: '4s' }}></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `
              linear-gradient(to right, #22c55e 1px, transparent 1px),
              linear-gradient(to bottom, #22c55e 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}></div>
          
          {/* Animated Light Rays */}
          <div className="absolute top-0 left-1/4 w-1 h-32 bg-gradient-to-b from-nexx-green/30 to-transparent animate-pulse" style={{ animationDuration: '3s', animationDelay: '1s' }}></div>
          <div className="absolute top-0 right-1/3 w-1 h-24 bg-gradient-to-b from-blue-400/20 to-transparent animate-pulse" style={{ animationDuration: '4s', animationDelay: '0.5s' }}></div>

          {/* Audio Waveform Elements - Reduced opacity for subtlety */}
          <div className="absolute top-1/6 left-1/2 transform -translate-x-1/2 w-4/5 h-12 flex items-center justify-center opacity-30">
            <div className="flex items-center space-x-0.5">
              {Array.from({ length: 80 }, (_, i) => {
                const height = Math.sin(i * 0.15) * 15 + Math.random() * 20 + 3;
                const delay = (i * 0.05).toFixed(2);
                return (
                  <div
                    key={i}
                    className="w-0.5 bg-nexx-green/60 animate-pulse rounded-sm"
                    style={{
                      height: `${height}px`,
                      animationDuration: `${1.2 + Math.random() * 0.8}s`,
                      animationDelay: `${delay}s`
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Secondary Waveform */}
          <div className="absolute bottom-1/6 right-1/3 w-2/3 h-10 flex items-center justify-center opacity-25">
            <div className="flex items-center space-x-0.5">
              {Array.from({ length: 60 }, (_, i) => {
                const height = Math.cos(i * 0.1) * 12 + Math.random() * 15 + 2;
                const delay = (i * 0.06).toFixed(2);
                return (
                  <div
                    key={i}
                    className="w-0.5 bg-nexx-green/50 animate-pulse rounded-sm"
                    style={{
                      height: `${height}px`,
                      animationDuration: `${1.0 + Math.random() * 1.0}s`,
                      animationDelay: `${delay}s`
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Infrastructure Done Right<br />
            <span className="text-nexx-green">Starts with a Conversation</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            We're not here to sell servers. <span className="relative inline-block text-nexx-green font-semibold drop-shadow-lg shadow-nexx-green/30 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-nexx-green after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left">We're here to solve problems.</span>
          </p>
          <Button 
            size="lg" 
            className="bg-nexx-green hover:bg-green-700 text-white px-8 py-4 text-lg ring-vibrate"
            data-tally-open="wbyBye" 
            data-tally-emoji-text="👋" 
            data-tally-emoji-animation="wave"
          >
            <Phone className="mr-2 h-5 w-5" />
            Book a Consultation
          </Button>
        </div>
      </section>

      {/* What We Help With Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <div className="inline-block mb-3">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 relative">
                What We Help With
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-0.5 bg-gradient-to-r from-nexx-green to-emerald-400 rounded-full"></div>
              </h2>
            </div>
            <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed relative overflow-hidden">
              <span className="inline-block animate-fade-in opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
                Strategic infrastructure challenges require 
              </span>
              <span className="inline-block animate-fade-in opacity-0 ml-1" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
                more than 
              </span>
              <span className="inline-block animate-fade-in opacity-0 text-nexx-green font-semibold ml-1 relative" style={{ animationDelay: '0.9s', animationFillMode: 'forwards' }}>
                commodity solutions
                <div className="absolute -bottom-0.5 left-0 w-full h-0.5 bg-gradient-to-r from-nexx-green to-emerald-400 scale-x-0 origin-left animate-grow-width" style={{ animationDelay: '1.2s', animationFillMode: 'forwards' }}></div>
              </span>
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {helpWith.map((item, index) => (
              <div
                key={index}
                className="group relative"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Card Glow Effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-nexx-green to-emerald-400 rounded-xl blur opacity-0 group-hover:opacity-30 transition-all duration-500 group-hover:blur-md"></div>
                
                <Card className="relative h-full bg-white/80 backdrop-blur-sm border-2 border-gray-100 hover:border-transparent transition-all duration-500 hover:shadow-2xl hover:shadow-nexx-green/10 transform hover:-translate-y-2 hover:scale-[1.02] overflow-hidden">
                  {/* Animated Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-5 transition-all duration-500`}></div>
                  
                  <CardHeader className="pb-3 relative z-10">
                    <div className="relative mb-3">
                      {/* Icon Container with Enhanced Animation */}
                      <div className={`
                        relative p-3 rounded-xl w-fit 
                        bg-gradient-to-br ${item.gradient} 
                        group-hover:scale-110 group-hover:rotate-3
                        transition-all duration-500 shadow-lg
                        group-hover:shadow-xl group-hover:shadow-current/25
                      `}>
                        <item.icon className="h-6 w-6 text-white relative z-10" />
                        
                        {/* Pulsing Ring Effect */}
                        <div className="absolute inset-0 rounded-xl bg-white/20 scale-0 group-hover:scale-110 group-hover:opacity-0 transition-all duration-500"></div>
                      </div>
                      
                      {/* Floating Particles */}
                      <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-nexx-green rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-300"></div>
                      <div className="absolute -bottom-1 -left-1 w-1 h-1 bg-emerald-400 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-300" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    
                    <CardTitle className="text-lg font-bold text-gray-900 group-hover:text-nexx-green transition-colors duration-300 leading-tight">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="relative z-10">
                    <CardDescription className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300 leading-relaxed text-sm">
                      {item.description}
                    </CardDescription>
                    
                    {/* Animated Progress Bar */}
                    <div className="mt-3 h-0.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full bg-gradient-to-r ${item.gradient} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out`}></div>
                    </div>
                  </CardContent>
                  
                  {/* Corner Accent */}
                  <div className="absolute top-0 right-0 w-12 h-12 overflow-hidden">
                    <div className={`absolute transform rotate-45 translate-x-6 -translate-y-6 w-6 h-6 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-20 transition-all duration-500`}></div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 relative section-pulse overflow-hidden">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              How It Works
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {howItWorks.map((step, index) => (
              <div key={index} className="text-center group">
                <div className="mb-4">
                  <div className="w-12 h-12 bg-nexx-green text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto group-hover:scale-110 transition-transform duration-300">
                    {step.step}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
        
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-nexx-green/5 rounded-full animate-pulse"></div>
          <div className="absolute top-3/4 right-1/4 w-24 h-24 bg-nexx-green/10 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-3/4 w-16 h-16 bg-nexx-green/5 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
        {/* Enhanced Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Gradient Orbs */}
          <div className="absolute top-1/4 left-1/6 w-40 h-40 bg-gradient-to-br from-nexx-green/10 to-emerald-400/10 rounded-full blur-3xl animate-float-up" style={{ animationDuration: '8s', animationDelay: '0s' }}></div>
          <div className="absolute top-2/3 right-1/4 w-32 h-32 bg-gradient-to-br from-blue-400/8 to-cyan-400/8 rounded-full blur-2xl animate-float-up" style={{ animationDuration: '12s', animationDelay: '2s' }}></div>
          <div className="absolute bottom-1/4 left-3/4 w-36 h-36 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl animate-float-up" style={{ animationDuration: '10s', animationDelay: '4s' }}></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `
              linear-gradient(to right, #22c55e 1px, transparent 1px),
              linear-gradient(to bottom, #22c55e 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }}></div>
          
          {/* Geometric Shapes */}
          <div className="absolute top-1/3 right-1/6 w-4 h-4 bg-nexx-green/20 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-1/3 left-1/5 w-3 h-3 bg-blue-400/30 rounded-full animate-pulse" style={{ animationDelay: '3s' }}></div>
          <div className="absolute top-1/2 left-1/3 w-2 h-2 bg-purple-400/25 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 relative">
                Flexible Engagement Formats
                <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-nexx-green via-emerald-400 to-nexx-green rounded-full"></div>
              </h2>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Choose the format that works best for your team and timeline. From quick strategic sessions to 
              <span className="text-nexx-green font-semibold"> comprehensive long-term partnerships</span>.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {formats.map((format, index) => (
              <div
                key={index}
                className="group relative"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Enhanced Card Glow Effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-nexx-green/20 via-emerald-400/20 to-nexx-green/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:blur-xl"></div>
                
                <Card className="relative h-full bg-white/90 backdrop-blur-sm border-2 border-gray-200/50 hover:border-transparent transition-all duration-700 hover:shadow-2xl hover:shadow-nexx-green/15 transform hover:-translate-y-3 hover:scale-[1.02] overflow-hidden group">
                  {/* Dynamic Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${format.gradient} opacity-0 group-hover:opacity-5 transition-all duration-700`}></div>
                  
                  {/* Floating Particles Effect */}
                  <div className="absolute top-3 right-3 w-2 h-2 bg-nexx-green/30 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div className="absolute top-5 right-6 w-1.5 h-1.5 bg-emerald-400/40 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500" style={{ animationDelay: '0.3s' }}></div>
                  
                  <CardContent className="p-4 relative z-10">
                    <div className="relative mb-3">
                      {/* Enhanced Icon Container */}
                      <div className={`
                        relative p-2.5 rounded-xl w-fit 
                        bg-gradient-to-br ${format.gradient} 
                        group-hover:scale-110 group-hover:rotate-6
                        transition-all duration-700 shadow-lg
                        group-hover:shadow-2xl group-hover:shadow-current/30
                      `}>
                        <format.icon className="h-5 w-5 text-white relative z-10" />
                        
                        {/* Multi-layer Ring Effects */}
                        <div className="absolute inset-0 rounded-xl bg-white/20 scale-0 group-hover:scale-125 group-hover:opacity-0 transition-all duration-700"></div>
                        <div className="absolute inset-0 rounded-xl bg-white/10 scale-0 group-hover:scale-150 group-hover:opacity-0 transition-all duration-900" style={{ transitionDelay: '0.1s' }}></div>
                      </div>
                    </div>
                    
                    <h3 className="text-base font-bold text-gray-900 group-hover:text-nexx-green transition-colors duration-500 mb-2 leading-tight">
                      {format.title}
                    </h3>
                    
                    <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-500 leading-relaxed text-sm mb-2">
                      {format.description}
                    </p>
                    
                    {/* Enhanced Progress Bar with Multiple Layers */}
                    <div className="relative">
                      <div className="h-0.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full bg-gradient-to-r ${format.gradient} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 ease-out`}></div>
                      </div>
                      <div className="absolute inset-0 h-0.5 bg-gradient-to-r from-white/50 to-transparent rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700" style={{ transitionDelay: '0.3s' }}></div>
                    </div>
                  </CardContent>
                  
                  {/* Enhanced Corner Accent with Animation */}
                  <div className="absolute top-0 right-0 w-12 h-12 overflow-hidden">
                    <div className={`absolute transform rotate-45 translate-x-6 -translate-y-6 w-6 h-6 bg-gradient-to-br ${format.gradient} opacity-0 group-hover:opacity-30 transition-all duration-700 group-hover:scale-150`}></div>
                  </div>
                  
                  {/* Bottom Edge Highlight */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-nexx-green/50 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700"></div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white relative grid-overlay">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Stop Guessing and Start Scaling?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's talk. Whether it's one call or full execution—we're in your corner.
          </p>
          <Button 
            size="lg" 
            className="bg-nexx-green hover:bg-green-700 text-white px-8 py-4 text-lg ring-vibrate"
            data-tally-open="wbyBye" 
            data-tally-emoji-text="👋" 
            data-tally-emoji-animation="wave"
          >
            <Phone className="mr-2 h-5 w-5" />
            Book Your Consultation
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Consultation;
